import { Field } from '@components/Field.tsx'
import styles from './TimingFields.module.css'

// Prep / cook / serves as a row of number inputs, shared by the new-recipe
// form and the detail page's inline editor.
//
// Held as raw strings while editing: a half-typed value isn't a valid number
// yet, and an emptied field has to mean "unset" rather than 0.
export type TimingDraft = { prep: string; cook: string; servings: string }

type Timings = {
	prepTimeMins: number | null
	cookTimeMins: number | null
	servings: number | null
}

const FIELDS: Array<{ key: keyof TimingDraft; label: string; min: number }> = [
	{ key: 'prep', label: 'Prep (min)', min: 0 },
	{ key: 'cook', label: 'Cook (min)', min: 0 },
	{ key: 'servings', label: 'Serves', min: 1 },
]

export const emptyTimingDraft = (): TimingDraft => ({
	prep: '',
	cook: '',
	servings: '',
})

export const toTimingDraft = (t: Timings): TimingDraft => ({
	prep: t.prepTimeMins === null ? '' : String(t.prepTimeMins),
	cook: t.cookTimeMins === null ? '' : String(t.cookTimeMins),
	servings: t.servings === null ? '' : String(t.servings),
})

// Blank or nonsense reads as "unset" — all three fields are optional, so a
// bad value is dropped rather than failing the save. 0 collapses to null too:
// the UI hides a zero time anyway, and `servings` must be positive.
const toIntOrNull = (s: string): number | null => {
	const t = s.trim()
	if (t === '') return null
	const n = Number(t)
	return Number.isInteger(n) && n > 0 ? n : null
}

export const fromTimingDraft = (d: TimingDraft): Timings => ({
	prepTimeMins: toIntOrNull(d.prep),
	cookTimeMins: toIntOrNull(d.cook),
	servings: toIntOrNull(d.servings),
})

type Props = {
	value: TimingDraft
	onChange: (next: TimingDraft) => void
}

export const TimingFields = ({ value, onChange }: Props) => (
	<div className={styles.row}>
		{FIELDS.map((f) => (
			<Field key={f.key} label={f.label} variant="caption">
				<input
					type="number"
					inputMode="numeric"
					min={f.min}
					value={value[f.key]}
					onChange={(e) =>
						onChange({ ...value, [f.key]: e.target.value })
					}
				/>
			</Field>
		))}
	</div>
)
